import "./Societies.scss";
import {
  useLazyGetAwardsBySocietyNumberQuery,
  useLazyGetSocietyByIdQuery,
  useGetAllSocietiesQuery,
  useLazyGetAwardeeByIdNameQuery,
  useGetAllcountsQuery,
} from "../../services/rtkQueryServices/checkApiService";
import { useEffect, useState } from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import { Box, Stack, Typography } from "@mui/material";
import ComboBox from "../Shared/Widgets/Autocomplete/Autocomplete";
import { useNavigate } from "react-router-dom";
import { bracketed } from "../../utils/format";

const formatCount = (n) =>
  n === null || n === undefined ? "-" : Number(n).toLocaleString();

// Society award-type flags (value "checked" when the society gives that kind of award).
const AWARD_TYPE_FIELDS = [
  ["Award_Leadership", "Leadership"],
  ["Award_ResearchFunds", "Research Funds"],
  ["Awards_Diversity", "Diversity"],
  ["Awards_Mentoring", "Mentoring"],
  ["Awards_Student", "Student"],
  ["Awards_Teaching", "Teaching"],
  ["Awards_Travel", "Travel"],
  ["Awards_Women", "Women"],
  ["Awards_Young", "Young Investigator"],
  ["Awards_News_Books", "News & Books"],
];

const TOTAL_BY_YEAR_PATTERN = /^total_(?:men|women)_(\d{4})$/;

const getLastCompleteYear = () => new Date().getFullYear() - 1;

const buildSocietyGraphData = (
  societyRows = [],
  maxYear = getLastCompleteYear()
) => {
  const graphData = [];

  societyRows.forEach((data) => {
    const years = Object.keys(data).reduce((yearSet, key) => {
      const match = key.match(TOTAL_BY_YEAR_PATTERN);
      if (!match) {
        return yearSet;
      }

      const year = Number(match[1]);
      if (year > maxYear) {
        return yearSet;
      }

      const menTotal = data[`total_men_${year}`];
      const womenTotal = data[`total_women_${year}`];
      if (menTotal !== null && menTotal !== undefined) {
        yearSet.add(year);
      }
      if (womenTotal !== null && womenTotal !== undefined) {
        yearSet.add(year);
      }

      return yearSet;
    }, new Set());

    [...years]
      .sort((a, b) => a - b)
      .forEach((year) => {
        graphData.push({
          "Men-Gender Neutral": data[`total_men_${year}`] ?? 0,
          "Women-Gender Neutral": data[`total_women_${year}`] ?? 0,
          year,
        });
      });
  });

  return graphData;
};

const Societies = () => {
  const [setSociety, { data: societyData }] = useLazyGetSocietyByIdQuery();
  const [societyGraphData, setSocietyGraphData] = useState([]);
  const { data: allSocietiesData } = useGetAllSocietiesQuery();
  const [setAwardData, { data: awardData }] = useLazyGetAwardeeByIdNameQuery();
  const { data: countData } = useGetAllcountsQuery();
  const [triggerFetchAwardsBySociety, { data: awardsData }] =
    useLazyGetAwardsBySocietyNumberQuery();
  const [awards, setAwards] = useState();
  const [currentAwardData, setCurrentAwardData] = useState();
  const [selectedSociety, setSelectedSociety] = useState({
    id: 1545,
    label: "American Physical Society (APS)",
  });
  const navigate = useNavigate();

  useEffect(() => {
    setSociety(1545);
    triggerFetchAwardsBySociety(1545);
  }, [setSociety, triggerFetchAwardsBySociety]);

  useEffect(() => {
    if (awardsData?.data) {
      const formattedData = awardsData.data.map((award) => ({
        awardName: award.Award,
        awardkind:
          award.Amount === "Unspecified" ? "Recognition" : award.Amount,
        category: award.Category,
        level: award.Level,
        awardNotes: award.Notes,
        percentageWomen: award.PercentageWomen,
        firstYearRecorded: award.firstYearRecorded,
        id: award.pk_award,
        website: award.Website,
      }));
      setAwards(formattedData);
    }
  }, [awardsData]);

  useEffect(() => {
    if (societyData) {
      setSocietyGraphData(buildSocietyGraphData(societyData));
    }
  }, [societyData]);

  useEffect(() => {
    if (awardData && currentAwardData?.id) {
      // Award detail now lives at /award/:id (the old /award route is gone).
      navigate(`/award/${currentAwardData.id}`, {
        state: { currentAwardData },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awardData, navigate, currentAwardData]);

  const valueFormatter = (value) =>
    value == null ? "" : `${Number(value).toLocaleString()} people`;

  // Thin the year axis to every 5th label; keep the full year in tooltips.
  const yearTick = (year, ctx) =>
    ctx?.location === "tick"
      ? Number(year) % 5 === 0
        ? `'${String(year).slice(2)}`
        : ""
      : String(year);

  const handleSocietySelect = (value) => {
    setSelectedSociety(value);
    setSociety(value?.id);
    triggerFetchAwardsBySociety(value?.id);
  };

  const handleAwardClick = (awardDetails) => {
    setCurrentAwardData(awardDetails);
    setAwardData(awardDetails?.id);
  };

  // Peak stacked value across years. The y axis never scales below 5 people,
  // so tiny societies (1-2 recipients in a year) don't render full-height bars.
  const peakPeople = societyGraphData.reduce(
    (max, d) =>
      Math.max(
        max,
        (d["Men-Gender Neutral"] || 0) + (d["Women-Gender Neutral"] || 0)
      ),
    0
  );

  const chartSetting = {
    yAxis: [
      {
        label: "Number of People",
        ...(peakPeople < 5 ? { min: 0, max: 5 } : {}),
      },
    ],
    xAxis: [
      {
        label: "Year",
        scaleType: "band",
        dataKey: "year",
        tickPlacement: "middle",
        valueFormatter: yearTick,
      },
    ],
    // Polished bars: rounded tops + light horizontal grid, matching the award page.
    borderRadius: 5,
    grid: { horizontal: true },
    height: 320,
    sx: {
      [`.${axisClasses.left} .${axisClasses.label}`]: {
        transform: "translate(-6px, 0)",
      },
    },
  };

  const heroStats = [
    { label: "Societies", value: formatCount(countData?.unique_societies_count) },
    { label: "Awards", value: formatCount(countData?.unique_awards_count) },
    { label: "Awardees", value: formatCount(countData?.unique_awardees_count) },
  ];

  // Selected-society dossier (the /society/<id> endpoint returns a single-row list).
  const society = societyData?.[0] || {};
  const awardCount = awards?.length ?? society.NumberOfAwards;
  const factLine = bracketed([
    society.Discipline,
    awardCount != null
      ? `${awardCount} ${awardCount === 1 ? "award" : "awards"}`
      : null,
    society.earliest_year ? `Since ${society.earliest_year}` : null,
    society.AddressCity1 && society.AddressState1
      ? `${society.AddressCity1}, ${society.AddressState1}`
      : null,
  ]);
  const description =
    society.Notes && society.Notes !== "None" ? society.Notes : null;
  const awardAreas = AWARD_TYPE_FIELDS.filter(
    ([field]) => String(society[field]).toLowerCase() === "checked"
  ).map(([, label]) => label);

  return (
    <div className="p-3 h-100 overflow-auto org-landing">
      <Box
        className="org-hero"
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: "16px",
          p: { xs: 3, md: 4 },
          mb: 3,
          color: "#fff",
          background: (t) =>
            `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
          boxShadow: "0 10px 30px rgba(16,24,40,0.12)",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -60,
            right: -40,
            width: 220,
            height: 220,
            borderRadius: "50%",
            bgcolor: "secondary.main",
            opacity: 0.18,
          }}
        />
        <Box sx={{ position: "relative" }}>
          <Typography
            variant="overline"
            sx={{ letterSpacing: "0.18em", fontWeight: 700, color: "secondary.main" }}
          >
            Recognizing Academic Scholars
          </Typography>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, mt: 0.5, mb: 1.5, maxWidth: 720, lineHeight: 1.15 }}
          >
            Explore scholarly awards across academic societies
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 640, mb: 3 }}>
            Browse honors and recognitions from scientific and professional
            societies worldwide, and see how representation has changed over time.
          </Typography>
          <Stack direction="row" spacing={{ xs: 3, md: 5 }} flexWrap="wrap" rowGap={2}>
            {heroStats.map((s) => (
              <Box key={s.label}>
                <Typography sx={{ fontWeight: 800, fontSize: "1.9rem", lineHeight: 1 }}>
                  {s.value}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.85,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      <div className="society-main">
        <div className="society-left">
      <div className="society-selector">
        <Typography
          variant="overline"
          sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: "0.1em" }}
        >
          Browse by society
        </Typography>
        <ComboBox
          autoCompleteOptions={allSocietiesData?.data || []}
          autoCompleteLabel={`Search societies`}
          onSelect={(value) => handleSocietySelect(value)}
          value={selectedSociety}
          width="100%"
        />
      </div>

      <div className="society-facts">
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          {society.Organization || selectedSociety?.label}
        </Typography>
        {factLine && (
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            {factLine}
            {society.WebAddress && (
              <>
                {"  ·  "}
                <Box
                  component="a"
                  href={society.WebAddress}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "secondary.main",
                    fontWeight: 600,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Website ↗
                </Box>
              </>
            )}
          </Typography>
        )}
        {description && (
          <Typography variant="body2" className="society-desc" sx={{ mt: 1.25 }}>
            {description}
          </Typography>
        )}
        {awardAreas.length > 0 && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1.25, color: "text.secondary" }}
          >
            <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
              Award areas -{" "}
            </Box>
            {bracketed(awardAreas)}
          </Typography>
        )}
      </div>

        <div className="chart-panel">
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Representation over time
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Award recipients per year, by gender
          </Typography>
          {societyGraphData.length > 0 ? (
            <div className="bar-chart-wrapper">
              <BarChart
                dataset={societyGraphData}
                series={[
                  {
                    dataKey: "Men-Gender Neutral",
                    label: "Men",
                    stack: true,
                    color: "var(--primary-color)",
                    valueFormatter,
                  },
                  {
                    dataKey: "Women-Gender Neutral",
                    label: "Women",
                    stack: true,
                    color: "var(--accent)",
                    valueFormatter,
                  },
                ]}
                {...chartSetting}
              />
            </div>
          ) : societyData ? (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", py: 5, textAlign: "center" }}
            >
              No per-year recipient data recorded for this society.
            </Typography>
          ) : null}
        </div>
        </div>

        <div className="awards-panel">
          <div className="awards-list">
            <div className="awards-list-title">
              Awards{awards?.length ? ` (${awards.length})` : ""}
            </div>
            {awards &&
              awards.map((award) => {
                const meta = bracketed([
                  award.category,
                  award.level,
                  award.firstYearRecorded,
                ]);
                return (
                  <div
                    key={award.id}
                    className="award-row"
                    onClick={() => handleAwardClick(award)}
                  >
                    <div className="award-row-name">{award?.awardName}</div>
                    {meta && <div className="award-row-meta">{meta}</div>}
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Societies;
